const USER_MENTION_RE =
  /@user:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/g;

const MENTION_SPAN_RE =
  /<span[^>]*data-id="([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})"[^>]*>[\s\S]*?<\/span>/gi;

export type MentionLabel = {
  id: string;
  label: string;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function labelById(labels: MentionLabel[]): Map<string, string> {
  return new Map(labels.map((item) => [item.id, item.label]));
}

/** Persist mention chips as `@user:<uuid>` tokens. Display names are not stored. */
export function serializeMentions(html: string): string {
  return html.replace(MENTION_SPAN_RE, '@user:$1');
}

/**
 * Turn stored `@user:<uuid>` tokens into named mention chips for the editor and
 * comment view. Unknown ids stay as the raw token.
 */
export function hydrateMentionTokens(html: string, labels: MentionLabel[]): string {
  if (typeof html !== 'string' || html.length === 0 || labels.length === 0) {
    return html;
  }
  const names = labelById(labels);
  return html.replace(USER_MENTION_RE, (token, id: string) => {
    const label = names.get(id);
    if (!label) return token;
    return `<span data-type="mention" data-id="${id}" class="mention">@${escapeHtml(label)}</span>`;
  });
}

export function mentionLabelForId(id: string, labels: MentionLabel[]): string {
  return labels.find((item) => item.id === id)?.label ?? id;
}

export function htmlToPlainText(html: string): string {
  if (typeof html !== 'string') {
    throw new Error('htmlToPlainText requires a string.');
  }
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(?:p|h[1-3]|li|div|blockquote)>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Plain textarea text as rich-text HTML: blank lines split paragraphs, single newlines become breaks. */
export function plainTextToHtml(text: string): string {
  const paragraphs = text.replace(/\r\n/g, '\n').split(/\n{2,}/).map((part) => part.trim()).filter(Boolean);
  return paragraphs.map((part) => `<p>${escapeHtml(part).replace(/\n/g, '<br>')}</p>`).join('');
}

export function isRichTextEmpty(html: string): boolean {
  if (typeof html !== 'string' || html.trim().length === 0) return true;
  if (/<img\b/i.test(html)) return false;
  return htmlToPlainText(html).length === 0;
}

export type EditorStoredContent = {
  content: string;
  contentType: 'html' | 'markdown';
};

const MARKDOWN_MARKERS: RegExp[] = [
  /\*\*[^*\n]+\*\*/,
  /__[^_\n]+__/,
  /(^|\n)#{1,6} \S/,
  /(^|\n)[ \t]*[-*+] \S/,
  /(^|\n)[ \t]*\d+\. \S/,
  /(^|\n)>[ \t]*\S/,
  /`[^`\n]+`/,
  /\[[^\]\n]+\]\([^)\s]+\)/,
  /(^|\n)```/,
  /(^|\n)---[ \t]*(\n|$)/,
  // Block markers left mid-paragraph after HTML collapsed the line breaks.
  / #{2,6} /,
  /[.!?)\]] +(?:[-*+] |\d+\. )/,
];

const RICH_HTML_TAG =
  /<(strong|b|em|i|u|s|strike|h[1-3]|ul|ol|li|a|code|pre|blockquote|table|img|mark|span)\b/i;

function looksLikeHtml(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith('<') && trimmed.includes('>');
}

function hasMarkdownSyntax(value: string): boolean {
  return MARKDOWN_MARKERS.some((pattern) => pattern.test(value));
}

/** Keep paragraph breaks so Markdown inside plain `<p>` tags can be parsed. */
function htmlToLooseText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * A flattened heading can swallow the paragraph that followed it
 * (`### Review Revisit weekly…`). Split that paragraph back off.
 * Sentence-case titles stay intact; words inside parentheses do not count.
 */
function splitAbsorbedHeading(line: string): string {
  const match = /^(#{1,6} )(.*)$/.exec(line);
  if (!match) return line;
  const prefix = match[1];
  const words = match[2].split(/ +/).filter((word) => word.length > 0);
  let paren = 0;
  let seenLower = false;
  const headingWords: string[] = [];
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i] ?? '';
    const bare = word.replace(/[()"']/g, '');
    const isCapital = /^[A-Z0-9]/.test(bare);
    const isLower = /^[a-z]/.test(bare);
    if (paren === 0 && headingWords.length > 0 && isCapital) {
      const rest = words.slice(i).join(' ');
      const absorbedSentence = (rest.length > 40 || /[.!?]/.test(rest)) && /[a-z]/.test(rest);
      if (seenLower || absorbedSentence) {
        return `${prefix}${headingWords.join(' ')}\n\n${rest}`;
      }
    }
    headingWords.push(word);
    if (paren === 0 && isLower) seenLower = true;
    paren += (word.match(/\(/g) || []).length;
    paren -= (word.match(/\)/g) || []).length;
    if (paren < 0) paren = 0;
  }
  return line;
}

/**
 * HTML storage collapses Markdown line breaks into spaces, so `### Notes - item`
 * arrives as one paragraph. Put the block markers back on their own lines.
 * Already-broken lines (a heading at column 0) are left alone.
 */
function restoreCollapsedMarkdown(text: string): string {
  const withHeadings = text.replace(/[ \t]+(?=#{2,6} )/g, '\n\n');
  const withFirstItem = withHeadings.replace(
    /(^|\n)(#{1,6} [^\n]+?) +(?=(?:[-*+] |\d+\. ))/g,
    '$1$2\n',
  );
  const withItems = withFirstItem.replace(/([.!?)\]]) +(?=(?:[-*+] |\d+\. ))/g, '$1\n');
  return withItems
    .split('\n')
    .map((line) => splitAbsorbedHeading(line))
    .join('\n');
}

/**
 * Work-item descriptions are HTML, but some are stored as Markdown (or as
 * Markdown left inside plain paragraphs). Those should be parsed, not shown
 * with the markers still visible.
 */
export function editorContentFromStored(value: string): EditorStoredContent {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return { content: typeof value === 'string' ? value : '', contentType: 'html' };
  }
  if (!looksLikeHtml(value)) {
    return hasMarkdownSyntax(value)
      ? { content: restoreCollapsedMarkdown(value), contentType: 'markdown' }
      : { content: value, contentType: 'html' };
  }
  if (RICH_HTML_TAG.test(value)) {
    return { content: value, contentType: 'html' };
  }
  const text = htmlToLooseText(value);
  if (!hasMarkdownSyntax(text)) {
    return { content: value, contentType: 'html' };
  }
  return { content: restoreCollapsedMarkdown(text), contentType: 'markdown' };
}
